// App shell — header, sidebar nav, content area, footer.
// Sticky elements: header always visible; sidebar always visible on desktop.
// On mobile (1.A doesn't optimize for it but layout shouldn't break) the
// sidebar collapses to a horizontal nav bar.

import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useMe } from './auth';
import { api } from './api';

const NAV_ITEMS = [
  { to: '/',             label: 'Listings',    ownerOnly: false, alertsBadge: false },
  { to: '/alerts',       label: 'Alerts',      ownerOnly: false, alertsBadge: true  },
  // Stage 2 W3 — new-product ingest
  { to: '/new-products', label: 'New product', ownerOnly: false, alertsBadge: false },
  { to: '/jobs',         label: 'Jobs',        ownerOnly: false, alertsBadge: false },
  { to: '/mabang',       label: 'Mabang',      ownerOnly: false, alertsBadge: false },
  { to: '/mappings',     label: 'Mappings',    ownerOnly: false, alertsBadge: false },
  { to: '/logs',         label: 'Audit logs',  ownerOnly: false, alertsBadge: false },
  { to: '/users',        label: 'Users',       ownerOnly: true,  alertsBadge: false },
];

const UNREAD_POLL_MS = 60_000;

export default function Layout() {
  const { me, loading, error } = useMe();
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchUnread = () => {
      api.alertsUnreadCount().then(r => {
        if (cancelled) return;
        if (r.ok) setUnread(r.data.unread);
      }).catch(() => {/* swallow — nav badge is best-effort */});
    };
    fetchUnread();
    const handle = setInterval(fetchUnread, UNREAD_POLL_MS);
    // Refresh on tab focus too — operators flip back from another tab.
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(handle);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="font-semibold text-lg">Costume Tool</div>
          <div className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded">Stage 1.A</div>
        </div>
        <div className="text-sm text-gray-600">
          {loading ? (
            'Loading…'
          ) : error ? (
            <span className="text-red-600">{error}</span>
          ) : me ? (
            <>
              <span className="font-mono">{me.email}</span>
              <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded">{me.role}</span>
            </>
          ) : null}
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <nav className="w-48 bg-white border-r border-gray-200 py-4 px-2 flex-shrink-0">
          <ul className="space-y-0.5">
            {NAV_ITEMS.filter(item => !item.ownerOnly || me?.role === 'owner').map(item => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded text-sm flex items-center justify-between ${
                      isActive
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  <span>{item.label}</span>
                  {item.alertsBadge && unread !== null && unread > 0 && (
                    <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0 rounded-full font-medium tabular-nums">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white px-6 py-2 text-xs text-gray-400 flex justify-between">
        <span>
          Internal multi-channel listing console — Cloudflare-hosted. Spec:&nbsp;
          <code>specs/stage-1-spec.md</code>
        </span>
        <span>Track E Phase E2</span>
      </footer>
    </div>
  );
}
